import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./DoshaResult.css";
import { LuSparkles, LuUsers, LuLeaf, LuDroplet, LuSoup, LuClock, LuHeartPulse, LuBan, LuArrowUpLeft } from "react-icons/lu";
import { FaLeaf, FaTint } from "react-icons/fa";
import { MdChair } from "react-icons/md";
import { FaRegHeart } from "react-icons/fa";
import { FaRegLightbulb, FaShield } from "react-icons/fa6";
import { IoBookOutline } from "react-icons/io5";
import { FiAward } from "react-icons/fi";
import { MdOutlineShield } from "react-icons/md";
import { FaInfoCircle } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  getDoshaSuggestedProducts,
  getDoshaSuggestedBlogs,
  markAsDoshaQuizCompleted,
  generateDoshaQuizAsyncResult,
  unwrapAsyncPayload,
} from "./index";
import { MultiStepLoader as Loader} from "../../components/Loader/multi-step-loader";
import { trackPageView, trackConversion, trackEngagement } from "../../utils/gtm";
import {
  captureUtmAttribution,
  captureLocationAsync,
  getUtmAttributionFromSources,
} from "../../utils/utmAttribution";
// Default loading states fallback
const defaultLoadingStates = [
  {
    text: "Reviewing your answers…",
  },
  {
    text: "Spotting your unique patterns…",
  },
  {
    text: "Figuring out your dominant dosha…",
  },
  {
    text: "Personalizing your results…",
  },
  {
    text: "Almost done! Preparing your dosha guide…",
  },
];

const LINK_PREVIEW_TITLE =
  "Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today";
const LINK_PREVIEW_DESCRIPTION =
  "Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.";
const DEFAULT_RESULT_PREVIEW_IMAGE =
  "https://maharishiayurvedaindia.com/images/dosha-quiz-result-preview.jpg";

const getDominantDoshaPreviewImage = (dominantSection, quizData) => {
  const candidates = [
    dominantSection?.icon,
    dominantSection?.icon_url,
    dominantSection?.image,
    dominantSection?.image_url,
    dominantSection?.banner_image,
    dominantSection?.thumbnail,
    dominantSection?.og_image,
    quizData?.data?.og_image,
    DEFAULT_RESULT_PREVIEW_IMAGE,
  ];
  return candidates.find(
    (src) => typeof src === "string" && src.trim().length > 0
  );
};

const DoshaResult = () => {
  const location = useLocation();
  const { finalData, userInfo, quizData, utmAttribution: routeUtmAttribution } =
    location.state || {};
  const utmAttribution = getUtmAttributionFromSources(
    routeUtmAttribution,
    finalData?.additional_data
  );
  const attributionPayload = { attribution: utmAttribution };
  const { token, playerId, dominantDosha } = userInfo || {};
  
  const nextStepSections = quizData?.data?.content_managements?.filter(
    (item) => item.content_type === "next-step-section"
  ) || [];

  // These CMS entries power the two image cards shown before the legacy next
  // step section. The legacy section remains the fallback until both cards are
  // completely configured.
  const nextStepImageCardSections = quizData?.data?.content_managements?.filter(
    (item) => String(item?.content_type || "").toLowerCase() === "next-step-image-cards"
  ) || [];
  
  const whyChooseSections = quizData?.data?.content_managements?.filter(
    (item) => item.content_type === "why-choose-section"
  ) || [];

  const showBannerSection = quizData?.data?.content_managements?.find(
    (item) => String(item?.content_type || "").toLowerCase() === "showbanner"
  );

  const showBannerImageUrl =
    showBannerSection?.attachment_url ||
    (typeof showBannerSection?.urls === "string" ? showBannerSection.urls : "");

  // Get loading states from content management (single object with JSON array in description)
  const loadingStates = (() => {
    if (!quizData?.data?.content_managements || !Array.isArray(quizData.data.content_managements)) {
      return defaultLoadingStates;
    }

    const loadingStateField = quizData.data.content_managements.find(
      item => item.content_type === 'loading-state'
    );

    if (!loadingStateField) {
      return defaultLoadingStates;
    }

    try {
      let descriptionText = loadingStateField.description || loadingStateField.title || '';
      if (!descriptionText.trim()) {
        return defaultLoadingStates;
      }

  
      // Try parsing directly first
      let statesArray;
      try {
        statesArray = JSON.parse(descriptionText);
      } catch (parseError) {
        // If direct parse fails, try replacing escaped quotes
        console.log('Direct parse failed, trying with unescaped quotes');
        const unescaped = descriptionText.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
        statesArray = JSON.parse(unescaped);
      }
      
      if (Array.isArray(statesArray) && statesArray.length > 0) {
      return statesArray
          .filter(text => text && String(text).trim() !== '')
          .map(text => ({ text: String(text).trim() }));
      } else {
        console.warn('Parsed result is not a valid array or is empty:', statesArray);
      }
    } catch (error) {
      console.error('Error parsing loading states:', error);
      console.error('Field data:', loadingStateField);
      console.error('Description value:', loadingStateField.description);
    }

    return defaultLoadingStates;
  })();

  const getResultTextLabels = () => {
    const labels = {
      curatedProductsPrefix: '',
      curatedProductsSuffix: '',
      buyNowButton: '',
      readMoreAboutHeading: '',
      readMoreButton: '',
      productCollectionText: ''
    };

    if (!quizData?.data?.content_managements || !Array.isArray(quizData.data.content_managements)) {
      return labels;
    }

    const resultTextFields = quizData.data.content_managements.filter(
      field => field.content_type === 'result-text'
    );

    resultTextFields.forEach(field => {
      const textValue = field.description || field.title || '';
      switch (field.name) {
        case 'curated_products_prefix':
          labels.curatedProductsPrefix = textValue;
          break;
        case 'curated_products_suffix':
          labels.curatedProductsSuffix = textValue;
          break;
        case 'buy_now_button':
          labels.buyNowButton = textValue;
          break;
        case 'read_more_about_heading':
          labels.readMoreAboutHeading = textValue;
          break;
        case 'read_more_button':
          labels.readMoreButton = textValue;
          break;
        case 'product_collection_text':
          labels.productCollectionText = textValue;
          break;
        default:
          break;
      }
    });

    return labels;
  };

  const getCurrencySymbol = () => {
    if (!quizData?.data?.content_managements || !Array.isArray(quizData.data.content_managements)) {
      return '₹'; // Default fallback
    }

    const currencyField = quizData.data.content_managements.find(
      field => field.content_type === 'currency'
    );

     if (currencyField) {
      return currencyField.description || currencyField.name || '₹';
    }

    return '₹'; // Default fallback if no currency field found
  };
  
  const renderNextStepSection = () => {
    if (!nextStepSections || nextStepSections.length === 0) {
      return null;
    }
    
    const sectionHeading = nextStepSections.find(item => item.name === "section-heading");
    const stepTitles = nextStepSections.filter(item => item.name?.startsWith("step-title"));
    
    const iconMap = {
      0: <LuUsers />,
      1: <IoBookOutline />,
    };
    
    const buildStepCards = () => {
      const cards = [];
      
      stepTitles.forEach((titleItem, index) => {
        // Extract suffix from title name (e.g., "step-title" -> "", "step-title1" -> "1")
        const titleSuffix = titleItem.name?.replace("step-title", "") || "";
        
        // Find matching description and button with same suffix
        const descriptionItem = nextStepSections.find(
          (item) => item.name === `step-description${titleSuffix}`
        );
        
      
        let buttonItem = nextStepSections.find(
          (item) => item.name === `step-button${titleSuffix}`
        );
        
        if (!buttonItem && titleSuffix) {
          buttonItem = nextStepSections.find(
            (item) => item.name === "step-button"
          );
        }
        
        let buttonUrl = "";
        if (buttonItem) {
          buttonUrl = typeof buttonItem?.urls === 'string' 
            ? buttonItem.urls 
            : (buttonItem?.urls?.url || buttonItem?.urls?.button_url || "");
        }
        if (!buttonUrl && titleItem?.urls) {
          buttonUrl = typeof titleItem.urls === 'string' 
            ? titleItem.urls 
            : (titleItem.urls?.url || titleItem.urls?.button_url || "");
        }
        
        const buttonText = buttonItem?.title || buttonItem?.description || titleItem?.title || "Learn More";
        
        cards.push({
          id: titleItem.id || index,
          title: titleItem.title,
          description: descriptionItem?.title || descriptionItem?.description || "",
          buttonText: buttonText,
          buttonUrl: buttonUrl
        });
      });
      
      return cards;
    };
    
    const stepCards = buildStepCards();
    
    return (
      <section className="next-step-section">
        {sectionHeading && (
          <h2 className="section-heading">
            {sectionHeading.title}
          </h2>
        )}
        {stepCards.length > 0 && (
          <div className="next-step-cards">
            {stepCards.map((card, index) => (
              <div key={card.id || index} className="step-card">
                <div><div className="step-icon">
                  {iconMap[index] || <LuUsers />}
                </div>
                <div className="step-title">
                  {card.title}
                </div>
                {card.description && (
                  <p className="step-description">
                    {card.description}
                  </p>
                )}</div>
                {card.buttonText && (
                  <button 
                    className="step-button"
                    onClick={() => {
                      if (card.buttonUrl && typeof card.buttonUrl === 'string' && card.buttonUrl.trim()) {
                        trackEngagement('next_step_button_clicked', {
                          dominant_dosha: dominantDosha,
                          button_text: card.buttonText,
                          card_title: card.title
                        });
                        
                        try {
                          const url = card.buttonUrl.trim();
                          const finalUrl = url.startsWith("http://") || url.startsWith("https://")
                            ? url 
                            : `https://${url}`;
                          
                          // Validate URL before opening
                          new URL(finalUrl);
                          window.open(finalUrl, "_blank");
                        } catch (error) {
                          console.error('Invalid URL:', card.buttonUrl, error);
                        }
                      }
                    }}
                    disabled={!card.buttonUrl || !card.buttonUrl.trim()}
                  >
                    {card.buttonText}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };

  const renderNextStepImageCards = () => {
    if (!nextStepImageCardSections.length) return null;

    const getField = (name) => nextStepImageCardSections.find((item) => item.name === name);
    const getUrl = (field) => {
      if (typeof field?.urls === "string") return field.urls.trim();
      return field?.urls?.url || field?.urls?.button_url || "";
    };

    const cards = ["", "1"].map((suffix, index) => {
      const titleField = getField(`card-title${suffix}`);
      const descriptionField = getField(`card-description${suffix}`);
      const supportingTextField = getField(`card-supporting-text${suffix}`);
      const imageField = getField(`card-image${suffix}`);
      const buttonField = getField(`card-button${suffix}`);

      return {
        id: titleField?.id || index,
        title: titleField?.title?.trim() || "",
        description: descriptionField?.description?.trim() || descriptionField?.title?.trim() || "",
        supportingText: supportingTextField?.description?.trim() || supportingTextField?.title?.trim() || "",
        imageUrl: imageField?.attachment_url || getUrl(imageField),
        // Titles must be unique in Admin; use Description for the visible CTA
        // label so both cards can still display the same button text.
        buttonText: buttonField?.description?.trim() || buttonField?.title?.trim() || "",
        buttonUrl: getUrl(buttonField),
      };
    });

    // Do not partially replace the existing cards. Both new CMS cards need all
    // display values and an actionable button before this layout is shown.
    const isComplete = (card) =>
      card.title && card.description && card.supportingText && card.imageUrl && card.buttonText && card.buttonUrl;

    if (!cards.every(isComplete)) return null;

    const sectionHeading = getField("section-heading");

    return (
      <section className="next-step-image-section">
        {sectionHeading?.title && <h2 className="section-heading">{sectionHeading.title}</h2>}
        <div className="next-step-image-cards">
          {cards.map((card) => (
            <article key={card.id} className="next-step-image-card">
              <img className="next-step-image-card__image" src={card.imageUrl} alt={card.title} />
              <div className="next-step-image-card__content">
                <h3 className="next-step-image-card__title">{card.title}</h3>
                <p className="next-step-image-card__description">{card.description}</p>
                <p className="next-step-image-card__supporting-text">
                  <LuClock aria-hidden="true" />
                  <span>{card.supportingText}</span>
                </p>
                <button
                  className="next-step-image-card__button"
                  onClick={() => {
                    trackEngagement("next_step_image_card_button_clicked", {
                      dominant_dosha: dominantDosha,
                      button_text: card.buttonText,
                      card_title: card.title,
                    });

                    try {
                      const finalUrl = card.buttonUrl.startsWith("http://") || card.buttonUrl.startsWith("https://")
                        ? card.buttonUrl
                        : `https://${card.buttonUrl}`;
                      new URL(finalUrl);
                      window.open(finalUrl, "_blank", "noopener,noreferrer");
                    } catch (error) {
                      console.error("Invalid next-step card URL:", card.buttonUrl, error);
                    }
                  }}
                >
                  <span>{card.buttonText}</span>
                  <LuArrowUpLeft aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  };
  
  const renderWhyChooseSection = () => {
    if (!whyChooseSections || whyChooseSections.length === 0) {
      return null;
    }
    
    const sectionHeading = whyChooseSections.find(item => item.name === "section-heading");
    const benefitItems = whyChooseSections.filter(item => item.name?.startsWith("benefit-title"));
    
    const iconMap = {
      0: <FiAward />,
      1: <MdOutlineShield />,
      2: <FaRegHeart />,
      3: <FaRegLightbulb />,
    };
    
    return (
      <section className="why-choose-section">
        {sectionHeading && (
          <h2 className="section-heading">
            {sectionHeading.title}
          </h2>
        )}
        {benefitItems.length > 0 && (
          <div className="benefits-grid1">
            {benefitItems.map((item, index) => (
              <div key={item.id || index} className="benefit-item1">
                <div className="benefit-icon">
                  {iconMap[index] || <FiAward />}
                </div>
                <div className="benefit-title">
                  {item.title}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };
  
  // Helper function to get icon component based on icon name string
  const getAmaIcon = (iconName) => {
    const iconMap = {
      'leaf': <LuLeaf/>,
      'water': <LuDroplet/>,
      'soup': <LuSoup/>,
      'clock': <LuClock />,
      'heart': <LuHeartPulse />,
      'ban': <LuBan />,
      'arrowupleft': <LuArrowUpLeft/>,
      'award': <FiAward />,
      'chair': <MdChair />,
      'info': <FaInfoCircle />,
    };
    return iconMap[iconName?.toLowerCase()] || <FaLeaf />;
  };

  // Get Ama section content from content management
  const getAmaSectionContent = () => {
    const content = {
      sectionHeading: '',
      whatIsTitle: '',
      whatIsDescription: '',
      levelTitle: '',
      levelSubtitle: '',
      tipsTitle: '',
      tips: []
    };

    if (!quizData?.data?.content_managements || !Array.isArray(quizData.data.content_managements)) {
      return content;
    }

    const amaSectionFields = quizData.data.content_managements.filter(
      field => field.content_type === 'ama-section'
    );

    amaSectionFields.forEach(field => {
      switch (field.name) {
        case 'section-heading':
          content.sectionHeading = field.title || field.description || '';
          break;
        case 'what-is-title':
          content.whatIsTitle = field.title || '';
          content.whatIsDescription = field.description || '';
          break;
        case 'level-title':
          content.levelTitle = field.title || '';
          content.levelSubtitle = field.description || '';
          break;
        case 'tips-title':
          content.tipsTitle = field.title || field.description || '';
          break;
        default:
          // Handle tips (tip-1, tip-2, etc.)
          if (field.name?.startsWith('tip-')) {
            content.tips.push({
              icon: field.urls || 'leaf', // Store icon name in urls field
              title: field.title || '',
              description: field.description || '',
              sequence: field.sequence || 0
            });
          }
          break;
      }
    });

    // Sort tips by sequence
    content.tips.sort((a, b) => a.sequence - b.sequence);

    return content;
  };

  const getLinesFromText = (text = "") => {
    if (!text || typeof text !== "string") {
      return [];
    }

    return text
      .replace(/\/n/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  };

  const renderMultilineParagraphs = (text, className, style = {}) => {
    if (!text || typeof text !== "string") {
      return null;
    }

    const lines = getLinesFromText(text);
    if (!lines.length) {
      return (
        <p className={className} style={style}>
          {text.trim()}
        </p>
      );
    }

    return lines.map((line, index) => (
      <p key={`${className}-${index}`} className={className} style={style}>
        {line}
      </p>
    ));
  };

  const renderAmaSection = ({levelAdviceTitle, levelAdviceDescription}) => {
    // Get dynamic data from API (only level and level_advice)
    // const amaLevel = amaSection?.level || "";
    // const amaLevelAdvice = amaSection?.level_advice || "";
    // Get static content from content management 
    const cmsContent = getAmaSectionContent();

    // Don't render if no content from either source
    const hasApiData = levelAdviceTitle || levelAdviceDescription;
    const hasCmsData = cmsContent.sectionHeading || cmsContent.whatIsTitle || cmsContent.tips.length > 0;
    
    if (!hasApiData && !hasCmsData) {
      return null;
    }

    return (
      <section className="ama-section">
        {cmsContent.sectionHeading && <h2 className="section-heading">{cmsContent.sectionHeading}</h2>}
        
        {/* What is Ama Box */}
        {(cmsContent.whatIsTitle || cmsContent.whatIsDescription) && (
          <div className="ama-info-box">
            <div className="ama-info-header">
              <FaInfoCircle className="ama-info-icon" />
              {cmsContent.whatIsTitle && <h3 className="ama-info-title">{cmsContent.whatIsTitle}</h3>}
            </div>
            {cmsContent.whatIsDescription && (
              <div className="ama-info-description">
                {getLinesFromText(cmsContent.whatIsDescription).map((line, index, arr) => (
                  <p key={index} style={{ marginBottom: '0.5rem' }}>
                    {line.trim()}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ama Level Section */}
        {cmsContent.levelTitle && <h2 className="section-heading ama-level-heading">{cmsContent.levelTitle}</h2>}
        {cmsContent.levelSubtitle && <p className="ama-level-subtitle">{cmsContent.levelSubtitle}</p>}
        
        {/* Ama Level Box - Data from API */}
        <div className="ama-level-box">
            {levelAdviceTitle && <h3 className="ama-level-title">{levelAdviceTitle}</h3>}
            {levelAdviceDescription && (
              <div className="ama-level-advice">
                {getLinesFromText(levelAdviceDescription).map((line, index, arr) => (
                  <p key={index} style={{ marginBottom: index < arr.length - 1 ? '1rem' : 0 }}>
                    {line.trim()}
                  </p>
                ))}
              </div>
            )}
          </div>
        {/* {(levelAdviceTitle || levelAdviceDescription) && (
        
        )} */}

        {/* General Tips Section */}
        {cmsContent.tips && cmsContent.tips.length > 0 && (
          <>
            {cmsContent.tipsTitle && <h2 className="section-heading ama-tips-heading">{cmsContent.tipsTitle}</h2>}
            <div className="ama-tips-grid">
              {cmsContent.tips.map((tip, index) => (
                <div key={index} className="ama-tip-card">
                  <div className="ama-tip-header">
                    <div className="ama-tip-icon">{getAmaIcon(tip.icon)}</div>
                    <h3 className="ama-tip-title">{tip.title}</h3>
                  </div>
                  <p className="ama-tip-description">{tip.description}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    );
  };

 

  // Helper function to extract text from HTML and limit to 2 lines
  const getBlogPreview = (bodyHtml, description) => {
    // Use body_html if available, otherwise fall back to description
    const content = bodyHtml || description || '';
    
    if (!content) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Get text content and remove extra whitespace
    let text = tempDiv.textContent || tempDiv.innerText || '';
    text = text.replace(/\s+/g, ' ').trim();
    
    // Split into lines (approximately 100 characters per line for 2 lines)
    const maxLength = 200; // Approximate 2 lines
    if (text.length <= maxLength) {
      return text;
    }
    
    // Truncate to maxLength and add ellipsis
    return text.substring(0, maxLength).trim() + '...';
  };
  const [finalDominantDosha, setFinalDominantDosha] = useState();
  const [products, setProducts] = useState([]);
  const [productCollectionUrl, setProductCollectionUrl] = useState("");
  const [productCollectionText, setProductCollectionText] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [doshaResponse, setDoshaResponse] = useState({});
  const [loading, setLoading] = useState(true);
  const [planUrl, setPlanUrl] = useState("");
  const [consultationUrl, setConsultationUrl] = useState("");

  const CUSTOM_BLOG_PREFIX = "custom_blog_";

  const normalizeDoshaKey = (value = "") =>
    String(value)
      .toLowerCase()
      .trim()
      .replace(/^your dominant dosha is\s+/i, "");

  // CMS entries use content_type (or name) like custom_blog_vata / CUSTOM_BLOG_PITTA.
  // The part after custom_blog_ must match dominant dosha (case-insensitive).
  const getCustomBlogDoshaKey = (item) => {
    const contentType = String(item?.content_type || "").toLowerCase().trim();
    if (contentType.startsWith(CUSTOM_BLOG_PREFIX)) {
      return contentType.slice(CUSTOM_BLOG_PREFIX.length);
    }

    const name = String(item?.name || "").toLowerCase().trim();
    if (name.startsWith(CUSTOM_BLOG_PREFIX)) {
      return name.slice(CUSTOM_BLOG_PREFIX.length);
    }

    return null;
  };

  const blogMatchesDominantDosha = (blog, doshaKey) =>
    getCustomBlogDoshaKey(blog) === doshaKey;

  const isGenericCustomBlog = (item) =>
    String(item?.content_type || "").toLowerCase().trim() === "custom_blog";

  const mapCustomBlog = (blog) => ({
    id: blog.id,
    title: blog.title || blog.name || "",
    description: blog.description || "",
    url: blog.urls || "",
    image: {
      src: blog.attachment_url || "/demo-blog.png",
    },
  });

  // 1) Prefer dosha-specific CMS blogs (custom_blog_vata, etc.)
  // 2) Fall back to generic CMS blogs (content_type: custom_blog)
  const getCustomBlogs = (dosha) => {
    if (!quizData?.data?.content_managements || !Array.isArray(quizData.data.content_managements)) {
      return [];
    }

    const contentManagements = quizData.data.content_managements;
    const doshaKey = normalizeDoshaKey(dosha);

    if (doshaKey) {
      const doshaSpecificBlogs = contentManagements
        .filter((item) => blogMatchesDominantDosha(item, doshaKey))
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map(mapCustomBlog);

      if (doshaSpecificBlogs.length > 0) {
        return doshaSpecificBlogs;
      }
    }

    return contentManagements
      .filter(isGenericCustomBlog)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map(mapCustomBlog);
  };

  function handleQuizResult(data, isFinal = false) {
    if (!data) {
      setLoading(false);
      return;
    }

    const nextResponse = unwrapAsyncPayload(data) || {};
    setDoshaResponse(nextResponse);

    if (isFinal) {
      addFinalDoshaResult(nextResponse);
      fetchDoshaSuggestedProducts();
      
      // CMS: dosha-specific blogs first, then generic custom_blog; else API
      const customBlogs = getCustomBlogs(dominantDosha);
      if (customBlogs.length > 0) {
        setBlogs(customBlogs);
      } else {
        fetchDoshaSuggestedBlogs();
      }
      
      // Set loading to false after all API calls are initiated
      setTimeout(() => {
        setLoading(false);
      }, 1000); // Small delay to ensure all data is processed
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    captureUtmAttribution();
    captureLocationAsync();

    // Track page view for DoshaResult
    trackPageView('Dosha Result', '/dosha-quiz/dosha-result');
    
    // Track result page engagement
    trackEngagement('dosha_result_viewed', {
      dominant_dosha: dominantDosha,
      player_id: playerId
    });
    
    const submissionData = finalData
      ? {
          ...finalData,
          additional_data: {
            ...finalData.additional_data,
            ...attributionPayload,
          },
        }
      : finalData;

    generateDoshaQuizAsyncResult(submissionData, token, {
      signal: abortController.signal,
    })
      .then((result) => {
        handleQuizResult(result, true);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Error generating dosha quiz result:", error?.message || error);
        handleQuizResult(null, true);
      });

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    const customFaviconUrl = 'https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://maharishiayurvedaindia.com&size=64';
    const defaultFaviconUrl = '/favicon.ico';
    
    const pageTitle = 'Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today';
    const pageDescription = 'Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.';
    
    const originalTitle = document.title;
    const originalDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');
    
    document.title = pageTitle;
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);
    
    const setMetaProperty = (property, content) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    const currentUrl = window.location.href;
    setMetaProperty('og:title', pageTitle);
    setMetaProperty('og:description', pageDescription);
    setMetaProperty('og:type', 'website');
    setMetaProperty('og:url', currentUrl);
    
    const setMetaName = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', pageTitle);
    setMetaName('twitter:description', pageDescription);
    
    const existingLinks = Array.from(document.querySelectorAll("link[rel*='icon']"));
    const originalHrefs = existingLinks.map(link => ({
      element: link,
      href: link.href,
      rel: link.getAttribute('rel')
    }));
    
    existingLinks.forEach(link => {
      link.href = customFaviconUrl;
    });
    
    if (existingLinks.length === 0) {
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = 'image/x-icon';
      faviconLink.href = customFaviconUrl;
      document.head.appendChild(faviconLink);
    }

    return () => {
      document.title = originalTitle;
      const descMeta = document.querySelector('meta[name="description"]');
      if (descMeta && originalDescription) {
        descMeta.setAttribute('content', originalDescription);
      }
      
      const linksToRestore = document.querySelectorAll("link[rel*='icon']");
      linksToRestore.forEach((link, index) => {
        if (originalHrefs[index]) {
          link.href = originalHrefs[index].href;
        } else {
          link.href = defaultFaviconUrl;
        }
      });
    };
  }, []);

  const resultPayload = unwrapAsyncPayload(doshaResponse) || {};
  const dominantDoshaSection = resultPayload?.dominant_dosha_section || {};
  const balanceUnderstandingSection =
    resultPayload?.balance_understanding_section || {};
  const commonTraitsSection = resultPayload?.common_traits_section || {};
  const amaSection = resultPayload?.ama_section || {};
  const levelAdviceContent = resultPayload?.level_advice_content || {};
  const addFinalDoshaResult = async (rawResponse = doshaResponse) => {
    try {
      if (!localStorage.getItem("doshaCompleted")) {
        const normalizedDominantDosha = String(dominantDosha || "")
          .replace(/^your dominant dosha is\s+/i, "")
          .trim()
          .toLowerCase();
        setFinalDominantDosha(normalizedDominantDosha);
        const response = await markAsDoshaQuizCompleted(
          token,
          rawResponse,
          playerId,
          utmAttribution
        );
        console.log("response add final dosha result", response);
        localStorage.setItem("doshaCompleted", "true");
      }
    } catch (error) {
      console.error(
        "Error adding final dosha result:",
        error?.message || error
      );
    }
  };

  const fetchDoshaSuggestedProducts = async () => {
    try {
      const response = await getDoshaSuggestedProducts(
        token,
        finalDominantDosha || dominantDosha
      );
      if (response && response.data) {
        setProductCollectionUrl(response?.collection_url);
        setProductCollectionText(response?.collection_text);
        setPlanUrl(response?.plan_url);
        setConsultationUrl(response?.consultation_url);

        if (Array.isArray(response.data)) {
          setProducts(response.data);
        } else if (response.data[dominantDosha]) {
          setProducts(response.data[dominantDosha]);
        } else {
          console.warn(`No products found for dosha: ${dominantDosha}`);
          console.warn(
            "Available keys in response.data:",
            Object.keys(response.data)
          );
          setProducts([]);
        }
      } else {
        console.warn("Invalid response structure for products");
        setProducts([]);
      }
    } catch (error) {
      console.error(
        "Error fetching dosha suggested products:",
        error?.message || error
      );
      setProducts([]);
    }
  };

  const fetchDoshaSuggestedBlogs = async () => {
    try {
      const response = await getDoshaSuggestedBlogs(
        token,
        finalDominantDosha || dominantDosha
      );
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          setBlogs(response.data);
        } else if (response.data[dominantDosha]) {
          setBlogs(response.data[dominantDosha]);
        } else {
          console.warn(`No blogs found for dosha: ${dominantDosha}`);
          console.warn(
            "Available keys in response.data:",
            Object.keys(response.data)
          );
          setBlogs([]);
        }
      } else {
        console.warn("Invalid response structure for blogs");
        setBlogs([]);
      }
    } catch (error) {
      console.error(
        "Error fetching dosha suggested blogs:",
        error?.message || error
      );
      setBlogs([]);
    }
  };

  // Default values if response data is missing
  const title = dominantDoshaSection.title || "";
  const subtitle = dominantDoshaSection.subtitle || "";
  const description = dominantDoshaSection.description || "";

  const balanceTitle = balanceUnderstandingSection.title || "";
  const balancePara1 = balanceUnderstandingSection.paragraph_1 || "";
  const balancePara2 = balanceUnderstandingSection.paragraph_2 || "";

  const traitsTitle = commonTraitsSection.title || "";
  const traits = commonTraitsSection.traits || [];
  const levelAdviceTitle = levelAdviceContent.title || "";
  const levelAdviceDescription = levelAdviceContent.description || levelAdviceContent.Description || "";
 const pageTitle = title || 'Know Your Dominant Dosha & Start Your Ayurvedic Wellness Journey Today';
  const pageDescription = description || 'Take the Ayurvedic Dosha Quiz and get instant insights into your unique body type. Understand your Ama (toxin buildup), receive diet and lifestyle guidance, and explore personalized remedies to restore balance.';
  const ogImageUrl = getDominantDoshaPreviewImage(dominantDoshaSection, quizData);
  const linkPreviewTitle = LINK_PREVIEW_TITLE;
  const linkPreviewDescription = LINK_PREVIEW_DESCRIPTION;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={linkPreviewTitle} />
        <meta property="og:description" content={linkPreviewDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={linkPreviewTitle} />
        <meta name="twitter:description" content={linkPreviewDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <link rel="icon" href="https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://maharishiayurvedaindia.com&size=64" />
      </Helmet>
      <div className="wellness-container">
      {showBannerImageUrl && (
        <section className="top-banner-section">
          <img
            src={showBannerImageUrl}
            alt={showBannerSection?.title || "Dosha Banner"}
            className="top-banner-image"
          />
        </section>
      )}
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">{title}</h1>
            <h2 className="hero-subtitle">{subtitle}</h2>
            {renderMultilineParagraphs(description, "hero-description")}
          </div>

          {!showBannerImageUrl && (
            <div className="hero-image">
              <img src="/dosha-result-banner.png" alt="Dosha Result" />
            </div>
          )}
        </div>
      </section>
      {/* Understanding Section */}
      <section className="understanding-section">
        <h2 className="section-heading ">{balanceTitle}</h2>
        {renderMultilineParagraphs(balancePara1, "section-description")}
        {renderMultilineParagraphs(balancePara2, "section-description")}
      </section>
      {/* Traits Section */}
      <section className="traits-section">
        <h2 className="section-heading text-center">{traitsTitle}</h2>
        <div className="traits-grid">
          {traits.map((trait, index) => (
            <div key={index} className="trait-card">
              <div className="trait-title">{trait.trait_name}</div>
              <p className="trait-description">{trait.description}</p>
            </div>
          ))}
        </div>
      </section>
      {/* Ama Section */}
      {renderAmaSection({levelAdviceTitle, levelAdviceDescription})}
      {/* Next Step Section */}
      {renderNextStepImageCards() || renderNextStepSection()}
      {/* Why Choose Section */}
      {renderWhyChooseSection()}
      {/* Products Section */}
      {products?.length > 0 && (() => {
        const resultLabels = getResultTextLabels();
        const productsPrefix = resultLabels.curatedProductsPrefix || '';
        const productsSuffix = resultLabels.curatedProductsSuffix || 'Dosha';
        return (
          <section className="products-section">
            <h2 className="section-heading">
              {productsPrefix} {/* Products Section {userInfo?.dominantDosha || "Your"} {productsSuffix}*/}
            </h2>
            <Swiper
      slidesPerView={4}
      spaceBetween={20}
      navigation
      pagination={{ clickable: true }}
      autoplay={{ delay: 3000 }}
      loop={true}
      modules={[ Pagination, Autoplay]}
      breakpoints={{
        320: { slidesPerView: 1.2 },
        480: { slidesPerView: 1.5 },
        768: { slidesPerView: 2.5 },
        1024: { slidesPerView: 3 },
        1280: { slidesPerView: 4 },
      }}
      className="products-slider"
    >
      {products.map((product, index) => {
        const productUrl = product?.url
          ? product.url.startsWith("http")
            ? product.url
            : `https://${product.url}`
          : "";
        const openProduct = () => {
          trackEngagement("product_buy_clicked", {
            product_name: product.product_name || product.title,
            product_price: product.variants?.[0]?.price,
            dominant_dosha: dominantDosha,
            product_url: product?.url,
          });
        };
        const cardContent = (
          <div className="product-card-content">
            <img
              className="product-image"
              src={product.images?.[0]?.src || "/demo-product.png"}
              alt={product.product_name || product.title || "Product image"}
            />
            <div className="product-title">
              {product.product_name || product.title}
            </div>
            <div className="product-cta">
              <div className="product-price">
                {getCurrencySymbol()}
                {product.variants?.[0]?.price || "N/A"}
              </div>
              <span className="product-button">
                {getResultTextLabels().buyNowButton || "Buy Now"}
              </span>
            </div>
          </div>
        );

        return (
          <SwiperSlide key={product.id || index}>
            {productUrl ? (
              <a
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="product-card product-card-link"
                onClick={openProduct}
              >
                {cardContent}
              </a>
            ) : (
              <div className="product-card">{cardContent}</div>
            )}
          </SwiperSlide>
        );
      })}
    </Swiper>
           
           {/* Product Collection Button - Center of grid */}
           {productCollectionUrl && (() => {
              const resultLabels = getResultTextLabels();
              const collectionText = resultLabels.productCollectionText || productCollectionText || "More products";
              return (
                <a
                  href={productCollectionUrl.startsWith("http") 
                    ? productCollectionUrl 
                    : `https://${productCollectionUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="product-button product-collection-button"
                  onClick={() => {
                    trackEngagement('product_collection_clicked', {
                      collection_url: productCollectionUrl,
                      dominant_dosha: dominantDosha,
                      button_text: collectionText
                    });
                  }}
                >
                  {collectionText}
                </a>
              );
            })()}
        </section>
        );
      })()}
      {/* Blog Section */}
      {blogs?.length > 0 && (() => {
        const resultLabels = getResultTextLabels();
        return (
          <section className="blog-section">
            <h2 className="section-heading">
              {resultLabels.readMoreAboutHeading || 'Read More About Ayurvedic Wellness'}
            </h2>
          <div
            className={[
              "blog-grid",
              blogs.length === 4 && "blog-grid--four",
              blogs.length === 3 && "blog-grid--three",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {blogs.map((blog, index) => {
              const blogPreview = getBlogPreview(blog.body_html, blog.description);
              return (
                <div className="blog-card" key={blog.id || index}>
                  <div className="blog-card-content">
                    <img
                      className="blog-image"
                      src={blog.image?.src || "/demo-blog.png"}
                      alt={blog.title || "Blog image"}
                    />
                    <div className="blog-title">{blog.title}</div>
                    {blogPreview && (
                      <p className="blog-description">{blogPreview}</p>
                    )}
                  </div>
                  <button
                    className="blog-button"
                    onClick={() => {
                      const url = blog?.url?.startsWith("http")
                        ? blog.url
                        : `https://${blog?.url}`;
                      
                      trackEngagement('blog_read_clicked', {
                        blog_title: blog.title,
                        blog_url: url,
                        dominant_dosha: dominantDosha
                      });
                      
                      window.open(url, "_blank");
                    }}
                  >
                    {getResultTextLabels().readMoreButton || 'Read More'}
                  </button>
                </div>
              );
            })}
            </div>
          </section>
        );
      })()}
    
     
      <Loader 
        loadingStates={loadingStates} 
        loading={loading} 
        duration={5000} 
        startIndex={0}
        loopStartIndex={loadingStates.length - 1}
      />
      </div>
    </>
  );
};

export default DoshaResult;
